/* eslint-disable camelcase */
import React, { memo } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useSelector, shallowEqual } from 'react-redux';
import moment from 'moment-timezone';

import KebobIcon from '../../assets/images/more-horizontal.svg';
import { user } from '../../redux/user';
import utils from '../../utils/utils';
import { GLOBAL_CALENDAR_SHIFT_TYPES, PERMISSION } from '../../constants';
import AddSlotsMenuItem from './AddSlotsMenuItem';
import InviteHCPMenuItem from './InviteHCPMenuItem';
import GuaranteeShiftMenuItem from './GuaranteeShiftMenuItem';
import { isOpenSlot } from './shiftActionSlots';

const GlobalOverviewShiftMenu = ({ shift }) => {
	const userData = useSelector(user, shallowEqual);
	const facilityId = shift?.facility_id;
	const facilityPermissions = userData.permissionsByFacility[facilityId] ?? [];
	const facilityData = userData.facilitiesEnabled.find((facility) => Number(facility.id) === Number(facilityId));

	const onDemandRouteData = shift.routeData[GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND];
	const shiftHasUnfilledOnDemandSlots = !!onDemandRouteData && onDemandRouteData.rawShift.slots.some(isOpenSlot);
	const showInviteMenuItem =
		shift.isUpcomingShift &&
		(!shift.start_time_utc || moment.utc(shift.start_time_utc).add(2, 'hours').isAfter(moment.utc())) &&
		shift.shift_types.has(GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND) &&
		shiftHasUnfilledOnDemandSlots &&
		utils.isAuthorizedExact(PERMISSION.INVITE, facilityPermissions);
	const showGuaranteeMenuItem =
		shift.isUpcomingShift &&
		shift.shift_types.has(GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND) &&
		shiftHasUnfilledOnDemandSlots &&
		utils.isAuthorizedExact(PERMISSION.GUARANTEE, facilityPermissions) &&
		!shift.is_orientation;

	const hasOnDemandPermission =
		utils.isAuthorizedExact(PERMISSION.EDIT, facilityPermissions) && facilityData.allow_on_demand_shifts;
	const hasFlexPermission =
		utils.isAuthorizedExact(PERMISSION.FLEX_ADD_EDIT_SHIFT, facilityPermissions) && facilityData.allow_flex_shifts;
	const hasOnCallPermission =
		utils.isAuthorizedExact(PERMISSION.ON_CALL_ADD_EDIT_SHIFT, facilityPermissions) && facilityData.allow_on_call;
	const hasHybridPermission = hasOnDemandPermission && hasFlexPermission && facilityData.enable_hybrid_shifts;

	const managementCompanyPermissions = userData?.managementCompanyPermissions[facilityData.management_company_id] || [];
	const hasCreatePastFlexShiftsPermission = managementCompanyPermissions.includes(
		PERMISSION.PORTAL_CREATE_FLEX_PAST_SHIFTS
	);

	const showAddFlexOption =
		hasFlexPermission &&
		(!shift.end_time_utc ||
			(shift.isUpcomingShift && moment.utc(shift.end_time_utc).diff(moment.utc(), 'minutes') > 15) ||
			(hasCreatePastFlexShiftsPermission && shift.shift_types.has(GLOBAL_CALENDAR_SHIFT_TYPES.FLEX)));
	const showAddOnDemandOption =
		hasOnDemandPermission &&
		shift.isUpcomingShift &&
		(!shift.start_time_utc || moment.utc(shift.start_time_utc).add(2, 'hours').isAfter(moment.utc()));
	const showAddOnCallOption = showAddOnDemandOption && hasOnCallPermission && shift.is_on_call;
	const showAddHybridOption =
		hasHybridPermission &&
		shift.isUpcomingShift &&
		!shift.is_orientation &&
		!shift.is_on_call &&
		(shift.start_time_utc ? moment.utc(shift.start_time_utc).add(2, 'hours').isAfter(moment.utc()) : true);

	const showAddShiftMenuItem =
		(!shift.is_on_call && (showAddFlexOption || showAddOnDemandOption)) || showAddHybridOption || showAddOnCallOption;
	const shouldRenderMenu = showAddShiftMenuItem || showInviteMenuItem || showGuaranteeMenuItem;

	if (!shouldRenderMenu) {
		return null;
	}

	return (
		<div className="action-menu-container">
			<Dropdown>
				<Dropdown.Toggle className="action-menu">
					<img src={KebobIcon} alt="Show More" className="action-menu-icon" />
				</Dropdown.Toggle>
				<Dropdown.Menu>
					{showAddShiftMenuItem && [
						<AddSlotsMenuItem
							key="gs-add-slot-menu-item"
							shift={shift}
							facilityTimezone={facilityData.timezone}
							showAddFlexOption={showAddFlexOption}
							showAddOnDemandOption={showAddOnDemandOption}
							showAddOnCallOption={showAddOnCallOption}
							showAddHybridOption={showAddHybridOption}
						/>
					]}
					{showInviteMenuItem && <InviteHCPMenuItem shift={shift} />}
					{showGuaranteeMenuItem && <GuaranteeShiftMenuItem shift={shift} />}
				</Dropdown.Menu>
			</Dropdown>
		</div>
	);
};

export default memo(GlobalOverviewShiftMenu);
